/* globals localStorage */

import React, { useState } from 'react';
import { QueryClientProvider } from 'react-query';
import { css } from '@emotion/react';
import { Link } from 'react-router';
import { blocks } from '@birdi/theme';
import CardViewSVG from '@birdi/icons/card-view.svg';
import TableViewSVG from '@birdi/icons/list-view.svg';
import MapPinSVG from '@birdi/icons/map-pin.svg';
import CalendarSVG from '@birdi/icons/calendar.svg';
import GlobeSVG from '@birdi/icons/sites-globe.svg';
import type { MapSiteListItem } from '@birdi/js-sdk/src/public/map-sites';
import { useMetaSelector } from '../meta/reducer';
import { getRelativeTime } from '../dashboard-new/utils';
import { mapSitesQueryClient, useSitesList } from './queries';
import { CreateSiteModal } from './CreateSiteModal';
import { SiteMenu } from './SiteMenu';
import {
  buttonGreen,
  cardCSS,
  coverCSS,
  emptyStateCSS,
  listRowCSS,
  metaRowCSS,
} from './styles';
import { hasSites } from './site-features';

type ViewMode = 'grid' | 'list';

const SiteCover = ({
  site,
  height,
}: {
  site: MapSiteListItem;
  height?: string;
}) => {
  const [errored, setErrored] = useState(false);
  // `||` (not `??`) so an empty-string URL also falls back to the placeholder;
  // onError covers a stale/404 cover URL.
  const src = site.s3ImageUrl || site.coverUrl || null;
  return (
    <div
      css={(theme) => css`
        ${coverCSS(theme)};
        ${height ? `height: ${height};` : ''}
      `}
    >
      {src && !errored ? (
        <img src={src} alt={site.name} onError={() => setErrored(true)} />
      ) : (
        <div
          css={(theme) => css`
            display: flex;
            align-items: center;
            justify-content: center;
            width: 100%;
            height: 100%;
            color: ${theme.mono40};
            svg {
              width: 2em;
              height: 2em;
            }
          `}
        >
          <GlobeSVG />
        </div>
      )}
    </div>
  );
};

const SiteCard = ({ site }: { site: MapSiteListItem }) => (
  <article css={cardCSS} data-testid="site-card">
    <div
      css={css`
        position: relative;
      `}
    >
      <Link to={`/sites/${site.id}`}>
        <SiteCover site={site} />
      </Link>
      <div
        css={css`
          position: absolute;
          top: 0.5rem;
          right: 0.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255, 255, 255, 0.85);
          border-radius: 6px;
          /* Only the trigger button (direct child of the menu container) —
             not the dropdown's item buttons, which stay left-aligned. */
          & > div > button {
            margin-left: 0;
            justify-content: center;
          }
        `}
      >
        <SiteMenu site={site} />
      </div>
    </div>
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: 0.4rem;
        padding: 0.75rem 1rem 1rem;
      `}
    >
      <Link to={`/sites/${site.id}`}>
        <h3
          css={(theme) => css`
            margin: 0;
            font-size: 1rem;
            color: ${theme.midnight};
          `}
        >
          {site.name}
        </h3>
      </Link>
      <div css={metaRowCSS}>
        <MapPinSVG />
        <span>{site.address || '—'}</span>
      </div>
      <div css={metaRowCSS}>
        <CalendarSVG />
        <span>
          Last activity{' '}
          {site.lastActivityAt ? getRelativeTime(site.lastActivityAt) : '—'}
        </span>
      </div>
    </div>
  </article>
);

const SiteRow = ({ site }: { site: MapSiteListItem }) => (
  <div css={listRowCSS} data-testid="site-row">
    <Link to={`/sites/${site.id}`}>
      <div
        css={css`
          width: 4.5rem;
          border-radius: 6px;
          overflow: hidden;
          flex-shrink: 0;
        `}
      >
        <SiteCover site={site} height="3rem" />
      </div>
    </Link>
    <Link
      to={`/sites/${site.id}`}
      css={css`
        min-width: 0;
      `}
    >
      <span
        css={(theme) => css`
          font-weight: bold;
          color: ${theme.midnight};
          white-space: nowrap;
        `}
      >
        {site.name}
      </span>
    </Link>
    <div css={metaRowCSS}>
      <MapPinSVG />
      <span>{site.address || '—'}</span>
    </div>
    <div css={metaRowCSS}>
      <CalendarSVG />
      <span>
        Last activity{' '}
        {site.lastActivityAt ? getRelativeTime(site.lastActivityAt) : '—'}
      </span>
    </div>
    <div
      css={css`
        margin-left: auto;
      `}
    >
      <SiteMenu site={site} />
    </div>
  </div>
);

const SitesContent = () => {
  const meta = useMetaSelector();
  const [createOpen, setCreateOpen] = useState(false);
  const [view, setView] = useState<ViewMode>(() => {
    const saved = localStorage.getItem('map-sites-view');
    return saved === 'list' ? 'list' : 'grid';
  });

  const { data: sites, isLoading } = useSitesList();

  const canCreate = meta.permissions.includes('workspace:map:create');

  const toggleView = () => {
    const next: ViewMode = view === 'grid' ? 'list' : 'grid';
    setView(next);
    localStorage.setItem('map-sites-view', next);
  };

  return (
    <div>
      <div
        css={css`
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin: 1em;
        `}
      >
        <h2
          css={css`
            margin: 0;
          `}
        >
          All Sites
        </h2>
        {canCreate && (
          <button
            type="button"
            css={buttonGreen}
            data-testid="create-site-button"
            onClick={() => setCreateOpen(true)}
          >
            Create site
          </button>
        )}
      </div>
      <div css={blocks.pageDescription}>
        <div
          css={css`
            display: flex;
            flex-direction: row;
            gap: 1rem;
            align-items: center;
            justify-content: space-between;
          `}
        >
          <p>
            A hub for the maps, reports, documents and reference sets at each
            location.
          </p>
          <button
            type="button"
            data-testid="sites-view-toggle"
            onClick={toggleView}
            css={(theme) => css`
              svg {
                color: ${theme.midnight};
              }
            `}
          >
            {view === 'grid' && (
              <CardViewSVG
                css={css`
                  font-size: 1.5em;
                `}
              />
            )}
            {view === 'list' && (
              <TableViewSVG
                css={css`
                  font-size: 1.5em;
                `}
              />
            )}
          </button>
        </div>
      </div>
      <div
        css={css`
          padding: 1rem;
        `}
      >
        {isLoading && (
          <div
            css={css`
              padding: 2em;
              text-align: center;
            `}
          >
            Loading ...
          </div>
        )}
        {sites && sites.length === 0 && (
          <div css={emptyStateCSS} data-testid="sites-empty-state">
            <h3>No site created yet</h3>
            <p>Create a site to get started.</p>
          </div>
        )}
        {sites && sites.length > 0 && view === 'grid' && (
          <div
            css={css`
              ${blocks.cardList};
              gap: 1rem;
            `}
          >
            {sites.map((site) => (
              <SiteCard key={site.id} site={site} />
            ))}
          </div>
        )}
        {sites && sites.length > 0 && view === 'list' && (
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 0.5rem;
            `}
          >
            {sites.map((site) => (
              <SiteRow key={site.id} site={site} />
            ))}
          </div>
        )}
      </div>
      <CreateSiteModal open={createOpen} setOpen={setCreateOpen} />
    </div>
  );
};

export const AllSites = () => {
  const meta = useMetaSelector();
  // Sites is a PLAN feature, so it follows the organisation's plan. `features`
  // is the merged list (plan features + org flags), not the org flags alone.
  if (!hasSites(meta.features)) {
    return (
      <p
        css={css`
          margin: 2em;
        `}
      >
        Sites are not enabled for this organisation.
      </p>
    );
  }
  return (
    <QueryClientProvider client={mapSitesQueryClient}>
      <SitesContent />
    </QueryClientProvider>
  );
};
